import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Server,
  FileCheck,
  ListTodo,
  FileText,
  AlertTriangle,
  ShieldAlert,
  CheckCircle,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Shield,
  User,
  Bell,
  ClipboardCheck,
  Bug,
  Brain,
  History,
  Users,
  Activity,
  TrendingUp,
  BookOpen,
  CalendarClock,
  Briefcase,
  GraduationCap,
  Building2,
  LifeBuoy,
  FileSearch,
  Scale,
  FolderOpen,
} from 'lucide-react';
import { cn, getRoleConfig } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { badgesApi } from '../lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { CopilotFAB } from './CopilotFAB';
import { useComplianceMode } from '../contexts/ComplianceModeContext';

const navigation = [
  // --- Overview (contíguo — comum a todos os modos) ---
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, section: 'Overview' },
  { name: 'Calendário', href: '/compliance/calendar', icon: CalendarClock, section: 'Overview' },
  { name: 'Órgão de Gestão', href: '/board', icon: Briefcase, section: 'Overview' },
  { name: 'Formação', href: '/training', icon: GraduationCap, section: 'Overview' },
  { name: 'Tarefas', href: '/tasks', icon: ListTodo, section: 'Overview', badgeKey: 'pending_reviews' },
  { name: 'Aprovações', href: '/approvals', icon: ClipboardCheck, section: 'Overview', badgeKey: 'pending_approvals' },
  { name: 'Documentos', href: '/documents', icon: FolderOpen, section: 'Overview' },
  { name: 'Notificações', href: '/notifications', icon: Bell, section: 'Overview' },
  { name: 'Relatórios', href: '/reports', icon: BarChart3, section: 'Overview' },
  // --- NIS2 (contíguo) ---
  { name: 'Ativos', href: '/assets', icon: Server, section: 'NIS2', nis2: true },
  { name: 'Requisitos NIS2', href: '/requirements', icon: FileCheck, section: 'NIS2', nis2: true },
  { name: 'Evidências', href: '/evidence', icon: FileText, section: 'NIS2', nis2: true },
  { name: 'Incidentes', href: '/incidents', icon: AlertTriangle, section: 'NIS2', badgeKey: 'pending_24h', nis2: true },
  { name: 'Vulnerabilidades', href: '/vulnerabilities', icon: Bug, section: 'NIS2', badgeKey: 'critical_vulns_open', critical: true, nis2: true },
  { name: 'Riscos', href: '/risks', icon: ShieldAlert, section: 'NIS2', nis2: true },
  { name: 'Continuidade', href: '/bcm', icon: LifeBuoy, section: 'NIS2', nis2: true },
  { name: 'Auditorias', href: '/audits', icon: FileSearch, section: 'NIS2', nis2: true },
  { name: 'Fornecedores', href: '/suppliers', icon: Building2, section: 'NIS2', nis2: true },
  { name: 'Políticas', href: '/policies', icon: BookOpen, section: 'NIS2', nis2: true },
  { name: 'DPO / RGPD', href: '/dpo', icon: Scale, section: 'NIS2', nis2: true },
  // --- EU AI Act ---
  { name: 'AI Act Dashboard', href: '/ai-act/dashboard', icon: LayoutDashboard, section: 'EU AI Act', aiAct: true },
  { name: 'Requisitos AI Act', href: '/ai-act/requirements', icon: FileCheck, section: 'EU AI Act', aiAct: true },
  { name: 'Sistemas IA', href: '/ai-act/systems', icon: Brain, section: 'EU AI Act', badgeKey: 'ai_unclassified', aiAct: true },
  { name: 'Riscos IA', href: '/ai-act/risks', icon: ShieldAlert, section: 'EU AI Act', aiAct: true },
  { name: 'Incidentes IA', href: '/ai-act/incidents', icon: AlertTriangle, section: 'EU AI Act', badgeKey: 'ai_incidents_pending', critical: true, aiAct: true },
  { name: 'Audit Trail IA', href: '/ai-act/audit', icon: History, section: 'EU AI Act', aiAct: true },
  { name: 'Post-Market', href: '/ai-act/post-market', icon: Activity, section: 'EU AI Act', aiAct: true },
  { name: 'Modelos GPAI', href: '/ai-act/gpai', icon: Brain, section: 'EU AI Act', aiAct: true },
  // --- Settings ---
  { name: 'Utilizadores', href: '/settings/users', icon: Users, section: 'Sistema', adminOnly: true },
  { name: 'Leads', href: '/settings/leads', icon: TrendingUp, section: 'Sistema', adminOnly: true },
  { name: 'Growth', href: '/settings/growth', icon: Activity, section: 'Sistema', adminOnly: true },
  { name: 'Insights', href: '/settings/insights', icon: BookOpen, section: 'Sistema', adminOnly: true },
  { name: 'Definições', href: '/settings', icon: Settings, section: 'Sistema' },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { showNis2, showAiAct, mode } = useComplianceMode();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({});
  const [tenantName, setTenantName] = useState(window.__nis2ai_tenant_name || null);
  const [tenantLogo, setTenantLogo] = useState(window.__nis2ai_tenant_logo || null);

  useEffect(() => {
    const handler = (e) => {
      setTenantName(e.detail.tenant_name || null);
      setTenantLogo(e.detail.branding?.logo_base64 || null);
    };
    window.addEventListener('nis2ai:branding-loaded', handler);
    return () => window.removeEventListener('nis2ai:branding-loaded', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      try { const res = await badgesApi.get(); setBadges(res.data); } catch { /* silent */ }
    };
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleInfo = getRoleConfig(user?.role);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:z-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
            <Link to="/dashboard" className="flex items-center gap-2">
              {tenantLogo ? (
                <img src={tenantLogo} alt="logo" className="w-8 h-8 rounded object-contain bg-white/10 p-0.5" />
              ) : (
                <div className="flex items-center justify-center w-8 h-8 rounded" style={{ background: 'var(--brand-primary, #2563eb)' }}>
                  <Shield className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex flex-col leading-tight">
                <span className="text-lg font-bold text-white font-heading" data-testid="app-brand-name">{tenantName || 'NIS2AI'}</span>
                {mode !== 'both' && (
                  <span
                    className="text-[9px] uppercase tracking-widest text-indigo-300 font-semibold"
                    data-testid="sidebar-mode-badge"
                    title={mode === 'ai_act_only' ? 'Modo apenas EU AI Act' : 'Modo apenas NIS2'}
                  >
                    {mode === 'ai_act_only' ? 'AI Act' : 'NIS2'}
                  </span>
                )}
              </div>
            </Link>
            <button
              className="lg:hidden text-slate-400 hover:text-white"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin" data-testid="sidebar-nav">
            {(() => {
              const isAdmin = user?.role === 'admin';
              const visibleNav = navigation.filter((it) => {
                if (it.adminOnly && !isAdmin) return false;
                if (it.nis2 && !showNis2) return false;
                if (it.aiAct && !showAiAct) return false;
                return true;
              });
              const sections = [];
              let currentSection = null;
              visibleNav.forEach((item) => {
                if (item.section !== currentSection) {
                  sections.push({ section: item.section, items: [] });
                  currentSection = item.section;
                }
                sections[sections.length - 1].items.push(item);
              });
              return sections.map((s, idx) => (
                <div key={`${s.section}-${idx}`} className={cn(idx > 0 && 'pt-3 mt-3 border-t border-slate-800')}>
                  <p className="px-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{s.section}</p>
                  {s.items.map((item) => {
                    const isActive = location.pathname === item.href ||
                      (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                    const count = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                    const hasCritical = item.critical && count > 0;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        data-testid={`nav-${item.href.replace(/\//g, '-').replace(/^-/, '')}`}
                        className={cn('sidebar-item flex items-center justify-between', isActive && 'active')}
                        onClick={onClose}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <item.icon className={cn('w-5 h-5 shrink-0', hasCritical && 'animate-pulse text-red-400')} />
                          <span className="truncate">{item.name}</span>
                        </span>
                        {count > 0 && (
                          <Badge
                            variant="outline"
                            data-testid={`badge-${item.badgeKey}`}
                            className={cn(
                              'h-5 min-w-[20px] px-1.5 text-[10px] font-bold border-0 shrink-0',
                              hasCritical ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-700 text-slate-100'
                            )}
                          >
                            {count > 99 ? '99+' : count}
                          </Badge>
                        )}
                      </Link>
                    );
                  })}
                </div>
              ));
            })()}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-2 py-2">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-slate-700 text-white text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || 'Utilizador'}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {roleInfo.label}
                </p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-800" data-testid="user-menu">
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Terminar Sessão
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export const Header = ({ onMenuClick, title, children }) => {
  return (
    <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200 lg:px-6">
      <div className="flex items-center gap-4">
        <button
          className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-md"
          onClick={onMenuClick}
        >
          <Menu className="w-5 h-5" />
        </button>
        {title && <h1 className="text-xl font-semibold text-slate-900 font-heading">{title}</h1>}
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </header>
  );
};

export const Layout = ({ children, title, headerContent }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header 
          onMenuClick={() => setSidebarOpen(true)} 
          title={title}
        >
          {headerContent}
        </Header>
        <main className="flex-1 p-4 lg:p-6">
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
      <CopilotFAB />
    </div>
  );
};

export default Layout;
